import {
  HUBBELL_FEEDBACK_DIVISIONS,
  MASS_RENEWAL_MAX_FEEDBACK_FILES,
  MASS_RENEWAL_MAX_EXCEPTION_FILES,
  productHoldDivision,
  productHoldItems,
  type FeedbackSupplierProfile,
  type HubbellFeedbackDivision,
} from '@erp/rpc';

import {
  hasHeaders,
  type SheetPeek,
  type WorkbookPeek,
} from '../hubbell-feedback/classifyFiles';

/**
 * Which Mass Renewal input a dropped file is, from its sheet names and first
 * rows (`peekWorkbook`), the way the run reads it on the server
 * (docs/pricing/mass-renewal-upload.md): the Results Workbook by its results
 * header, a Feedback File by its "<code> Feedback" sheet, the Exception List
 * by AS400 ITEM and SELL NET on its first sheet. The page never parses the
 * workbooks beyond that.
 */

/** How far down a sheet a header may sit (a marker row is above it); the run's HEADER_SEARCH_ROWS. */
export const RENEWAL_PEEK_ROWS = 5;

export type RenewalFileKind =
  | { kind: 'results' }
  | { kind: 'feedback'; division: HubbellFeedbackDivision }
  | { kind: 'exceptions'; holdDivision?: HubbellFeedbackDivision }
  | { kind: 'unknown' };

function headerWithin(sheet: SheetPeek, headers: readonly string[]): boolean {
  return sheet.rows
    .slice(0, RENEWAL_PEEK_ROWS)
    .some((row) => hasHeaders(row, headers));
}

/** "  wt   FEEDBACK " → "wt feedback": case and spacing do not tell sheets apart. */
function sheetKey(name: string): string {
  return name.trim().split(/\s+/).join(' ').toLowerCase();
}

/**
 * The Division a sheet name such as "WT Feedback" names: a known Division
 * code, then "Feedback", case and spacing aside (as the run reads it).
 */
export function feedbackSheetDivision(
  sheetName: string,
): HubbellFeedbackDivision | undefined {
  const key = sheetKey(sheetName);
  return HUBBELL_FEEDBACK_DIVISIONS.find(
    (division) => key === `${division.code.toLowerCase()} feedback`,
  );
}

export function classifyRenewalWorkbook(
  peek: WorkbookPeek,
  supplier: FeedbackSupplierProfile,
  fileName = '',
): RenewalFileKind {
  if (peek.sheets.some((sheet) => headerWithin(sheet, supplier.resultsHeader)))
    return { kind: 'results' };
  for (const sheet of peek.sheets) {
    const division = feedbackSheetDivision(sheet.name);
    if (division) return { kind: 'feedback', division };
  }
  const first = peek.sheets[0];
  if (
    first &&
    ['AS400 ITEM', 'Supplier Item'].some((item) =>
      ['SELL NET', 'Margin'].some((pricing) =>
        headerWithin(first, [item, pricing]),
      ),
    )
  )
    return { kind: 'exceptions' };
  const holdDivision = productHoldDivision(fileName, supplier.supplier);
  if (first && holdDivision && productHoldItems(first.rows))
    return { kind: 'exceptions', holdDivision };
  return { kind: 'unknown' };
}

/** A file on the card: classified, still being read, or one the browser could not open. */
export type DroppedRenewalFile = {
  id: string;
  file: File;
  kind: RenewalFileKind | { kind: 'reading' } | { kind: 'unreadable' };
};

/** Why the drop cannot start yet, in the order the card says them. */
export type RenewalDropIssue =
  | { issue: 'no results workbook' }
  | { issue: 'several results workbooks'; count: number }
  | { issue: 'no feedback file' }
  | { issue: 'too many feedback files' }
  | { issue: 'division has two feedback files'; code: string }
  | { issue: 'several exception lists'; count: number };

export interface RenewalDropState {
  files: DroppedRenewalFile[];
  resultsWorkbook: File | null;
  feedbackFiles: File[];
  exceptionLists: File[];
  /** A file is still being classified. */
  reading: boolean;
  /** What keeps Start disabled; empty when the drop may start. */
  issues: RenewalDropIssue[];
}

function filesOf(
  entries: DroppedRenewalFile[],
  kind: RenewalFileKind['kind'],
): File[] {
  return entries.flatMap((entry) =>
    entry.kind.kind === kind ? [entry.file] : [],
  );
}

/** Division codes two or more Feedback Files name, in drop order. */
function duplicatedDivisions(entries: DroppedRenewalFile[]): string[] {
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  for (const { kind } of entries) {
    if (kind.kind !== 'feedback') continue;
    const code = kind.division.code;
    if (seen.has(code)) duplicated.add(code);
    seen.add(code);
  }
  return [...duplicated];
}

function dropIssues(
  entries: DroppedRenewalFile[],
  counts: { results: number; feedback: number; exceptions: number },
): RenewalDropIssue[] {
  const issues: RenewalDropIssue[] = [];
  if (counts.results === 0) issues.push({ issue: 'no results workbook' });
  if (counts.results > 1)
    issues.push({ issue: 'several results workbooks', count: counts.results });
  if (counts.feedback === 0) issues.push({ issue: 'no feedback file' });
  if (counts.feedback > MASS_RENEWAL_MAX_FEEDBACK_FILES)
    issues.push({ issue: 'too many feedback files' });
  for (const code of duplicatedDivisions(entries))
    issues.push({ issue: 'division has two feedback files', code });
  if (counts.exceptions > MASS_RENEWAL_MAX_EXCEPTION_FILES)
    issues.push({ issue: 'several exception lists', count: counts.exceptions });
  return issues;
}

/**
 * What the drop amounts to: exactly one Results Workbook, one to nine
 * Feedback Files (one per Division) and up to twenty Exception Lists go in;
 * unrecognised and unreadable files are listed but never uploaded.
 */
export function renewalDropState(
  entries: DroppedRenewalFile[],
): RenewalDropState {
  const results = filesOf(entries, 'results');
  const feedbackFiles = filesOf(entries, 'feedback');
  const exceptions = filesOf(entries, 'exceptions');
  return {
    files: entries,
    resultsWorkbook: results.length === 1 ? (results[0] ?? null) : null,
    feedbackFiles,
    exceptionLists: exceptions,
    reading: entries.some((entry) => entry.kind.kind === 'reading'),
    issues: dropIssues(entries, {
      results: results.length,
      feedback: feedbackFiles.length,
      exceptions: exceptions.length,
    }),
  };
}
